import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { apiClient } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const COMPLIANCE_RULES: Array<[string, string, string]> = [
  ['third_party_disclosure', 'Third-party disclosure', 'CRITICAL'],
  ['identity_verification_missing', 'Identity verification missing', 'SERIOUS'],
  ['disclosure_after_third_party_confirmed', 'Disclosure after third party', 'CRITICAL'],
  ['repossession_misrepresentation', 'Repossession misrepresentation', 'SERIOUS'],
  ['threatening_or_coercive_language', 'Threatening or coercive language', 'SERIOUS'],
  ['harassment_or_abusive_language', 'Harassment or abusive language', 'SERIOUS'],
  ['payment_or_balance_misrepresentation', 'Payment or balance misrepresentation', 'SERIOUS'],
  ['required_disclosure', 'Required disclosure missing', 'WARNING'],
  ['unprofessional_language', 'Unprofessional language', 'WARNING'],
  ['other_high_risk_compliance_concern', 'Other high-risk concern', 'CRITICAL'],
];

const CATEGORIES = ['RISK', 'ESCALATION', 'COMPLIANCE', 'POSITIVE', 'CUSTOM'];
const MATCH_TYPES = ['KEYWORD', 'EXACT_PHRASE', 'PHRASE_SET'];
const SPEAKERS = ['AGENT', 'CUSTOMER', 'ANY'];

/** Campaign list for Call QA configuration. */
export const CallQaCampaignsPage: React.FC = () => {
  const { user } = useAuth();
  const canRead = (user?.permissions || []).includes('call_qa.scorecards.read');
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['callQaCampaignConfigs'],
    queryFn: () => apiClient.callQa.listCampaignConfigs(),
    enabled: canRead,
  });

  if (!canRead) {
    return <Alert severity="warning">Campaign QA requires call_qa.scorecards.read.</Alert>;
  }

  const rows = data?.data?.data?.rows || [];

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configure scorecard mode, thresholds, and phrase monitoring per campaign.
        Campaigns default to the Global scorecard until you opt into a campaign-specific card.
      </Typography>
      {isLoading ? <CircularProgress size={24} /> : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Campaign', 'Scorecard', 'Min Pass', 'Review ≤', 'Phrase Monitor', ''].map((h) => (
                  <TableCell key={h}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No active campaigns.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : rows.map((r: any) => (
                <TableRow key={r.campaign_id} hover>
                  <TableCell>{r.campaign_name}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={r.scorecard_mode === 'CAMPAIGN' ? 'Campaign' : r.scorecard_mode === 'SERVICE' ? 'Service' : 'Global'}
                      color={r.scorecard_mode === 'CAMPAIGN' ? 'primary' : r.scorecard_mode === 'SERVICE' ? 'secondary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{r.min_passing_score}</TableCell>
                  <TableCell>{r.review_threshold}</TableCell>
                  <TableCell>{r.phrase_monitoring_enabled === false ? 'Off' : 'On'}</TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => navigate(`/call-qa/campaigns/${r.campaign_id}`)}>
                      QA Configuration
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

/** Per-campaign QA configuration editor. */
export const CallQaCampaignDetailPage: React.FC = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const permissions = user?.permissions || [];
  const canRead = permissions.includes('call_qa.scorecards.read');
  const canManage = permissions.includes('call_qa.scorecards.manage');
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['callQaCampaignConfig', campaignId],
    queryFn: () => apiClient.callQa.getCampaignConfig(String(campaignId)),
    enabled: canRead && Boolean(campaignId),
  });

  const { data: servicesData } = useQuery({
    queryKey: ['callQaServices'],
    queryFn: () => apiClient.callQa.listServices(),
    enabled: canRead,
  });
  const services: any[] = servicesData?.data?.data || [];

  const cfg = data?.data?.data;
  const [mode, setMode] = useState<'GLOBAL' | 'CAMPAIGN' | 'SERVICE'>('GLOBAL');
  const [serviceId, setServiceId] = useState<string>('');
  const [minPass, setMinPass] = useState(80);
  const [reviewAt, setReviewAt] = useState(90);
  const [critBehavior, setCritBehavior] = useState<'FAIL' | 'REVIEW'>('FAIL');
  const [phraseOn, setPhraseOn] = useState(true);
  const [complianceRules, setComplianceRules] = useState<Record<string, { enabled: boolean; severity: string }>>({});
  const [desktopEnabled, setDesktopEnabled] = useState(false);
  const [desktopConsent, setDesktopConsent] = useState(false);
  const [desktopMode, setDesktopMode] = useState('DISABLED');
  const [recordingAllowed, setRecordingAllowed] = useState(false);
  const [recordingSource, setRecordingSource] = useState('MANUAL_ONLY');
  const [softphoneAllowlist, setSoftphoneAllowlist] = useState('');
  const [samplePercent, setSamplePercent] = useState(10);
  const [rules, setRules] = useState<any[]>([]);
  const [hydrated, setHydrated] = useState(false);

  React.useEffect(() => {
    if (!cfg || hydrated) return;
    setMode(cfg.scorecard_mode === 'CAMPAIGN' ? 'CAMPAIGN' : cfg.scorecard_mode === 'SERVICE' ? 'SERVICE' : 'GLOBAL');
    setServiceId(cfg.service_id || '');
    setMinPass(Number(cfg.min_passing_score ?? 80));
    setReviewAt(Number(cfg.review_threshold ?? 90));
    setCritBehavior(cfg.critical_failure_behavior === 'REVIEW' ? 'REVIEW' : 'FAIL');
    setPhraseOn(cfg.phrase_monitoring_enabled !== false);
    const savedRules = cfg.compliance_rules;
    const enabled = new Set(savedRules?.enabled_rule_ids || COMPLIANCE_RULES.map(([id]) => id));
    const nextRules: Record<string, { enabled: boolean; severity: string }> = {};
    COMPLIANCE_RULES.forEach(([id, , severity]) => {
      nextRules[id] = {
        enabled: savedRules?.enabled_rule_ids ? enabled.has(id) : true,
        severity: String(savedRules?.severity?.[id] || severity).toUpperCase(),
      };
    });
    setComplianceRules(nextRules);
    setDesktopEnabled(Boolean(cfg.desktop_recording_enabled));
    setDesktopConsent(Boolean(cfg.desktop_consent_confirmed));
    setDesktopMode(cfg.desktop_capture_mode || 'DISABLED');
    setRecordingAllowed(Boolean(cfg.recording_allowed));
    setRecordingSource(cfg.recording_source || 'MANUAL_ONLY');
    setSoftphoneAllowlist((cfg.softphone_process_allowlist || []).join(', '));
    setSamplePercent(Number(cfg.sampling_random_percent ?? 10));
    setRules((cfg.phrase_rules || []).map((r: any) => ({
      ...r,
      phrasesText: Array.isArray(r.phrases) ? r.phrases.join(', ') : '',
    })));
    setHydrated(true);
  }, [cfg, hydrated]);

  React.useEffect(() => {
    setHydrated(false);
  }, [campaignId]);

  const saveMutation = useMutation({
    mutationFn: () => apiClient.callQa.updateCampaignConfig(String(campaignId), {
      scorecard_mode: mode,
      service_id: mode === 'SERVICE' ? (serviceId || null) : null,
      min_passing_score: minPass,
      review_threshold: reviewAt,
      critical_failure_behavior: critBehavior,
      phrase_monitoring_enabled: phraseOn,
      compliance_rules: {
        enabled_rule_ids: COMPLIANCE_RULES.map(([id]) => id).filter((id) => complianceRules[id]?.enabled !== false),
        severity: Object.fromEntries(
          COMPLIANCE_RULES.map(([id, , fallback]) => [id, complianceRules[id]?.severity || fallback]),
        ),
      },
      desktop_recording_enabled: desktopEnabled,
      desktop_consent_confirmed: desktopConsent,
      desktop_capture_mode: desktopMode,
      recording_allowed: recordingAllowed,
      recording_source: recordingSource,
      softphone_process_allowlist: softphoneAllowlist
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      sampling_random_percent: samplePercent,
      phrase_rules: rules.map((r, idx) => ({
        category: r.category || 'CUSTOM',
        name: r.name,
        match_type: r.match_type || 'KEYWORD',
        phrases: String(r.phrasesText || '')
          .split(',')
          .map((p: string) => p.trim())
          .filter(Boolean),
        speaker_scope: r.speaker_scope || 'ANY',
        severity: r.severity || 'warning',
        enabled: r.enabled !== false,
        sort_order: (idx + 1) * 10,
      })),
      criteria: mode === 'CAMPAIGN' && cfg?.active_scorecard?.criteria
        ? cfg.active_scorecard.criteria
        : undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['callQaCampaignConfig', campaignId] });
      queryClient.invalidateQueries({ queryKey: ['callQaCampaignConfigs'] });
      setSaved(true);
      setError(null);
      setHydrated(false);
    },
    onError: (err: any) => {
      setError(err?.response?.data?.message || err?.message || 'Save failed');
    },
  });

  const weightPreview = useMemo(() => {
    const criteria = cfg?.active_scorecard?.criteria || [];
    const enabled = criteria.filter((c: any) => c.enabled !== false && Number(c.weight) > 0);
    return enabled.reduce((s: number, c: any) => s + Number(c.weight || 0), 0);
  }, [cfg]);

  if (!canRead) {
    return <Alert severity="warning">Campaign QA requires call_qa.scorecards.read.</Alert>;
  }
  if (isLoading || !cfg) return <CircularProgress size={24} />;

  return (
    <Box>
      <Button size="small" onClick={() => navigate('/call-qa/campaigns')} sx={{ mb: 1 }}>
        ← Back to Campaigns
      </Button>
      <Typography variant="h6" gutterBottom data-testid="campaign-qa-title">
        {cfg.campaign_name} — QA Configuration
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Resolved scorecard: {cfg.resolved_scorecard_source}
        {cfg.active_scorecard?.version_label ? ` (${cfg.active_scorecard.version_label})` : ''}
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {saved && <Alert severity="success" sx={{ mb: 1 }}>Campaign QA configuration saved.</Alert>}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid="campaign-scorecard-mode">
        <Typography variant="subtitle2" gutterBottom>Scorecard</Typography>
        <FormControl size="small" sx={{ minWidth: 280, mb: 1 }}>
          <InputLabel>Scorecard mode</InputLabel>
          <Select
            label="Scorecard mode"
            value={mode}
            disabled={!canManage}
            onChange={(e) => { setSaved(false); setMode(e.target.value as any); }}
          >
            <MenuItem value="GLOBAL">Use Global Scorecard</MenuItem>
            <MenuItem value="SERVICE">Use Service Scorecard</MenuItem>
            <MenuItem value="CAMPAIGN">Use Campaign Scorecard</MenuItem>
          </Select>
        </FormControl>
        {mode === 'SERVICE' && (
          <FormControl size="small" sx={{ minWidth: 280, mb: 1, ml: { sm: 2 } }}>
            <InputLabel>Service</InputLabel>
            <Select
              label="Service"
              value={services.some((s) => s.id === serviceId) ? serviceId : ''}
              disabled={!canManage}
              onChange={(e) => { setSaved(false); setServiceId(String(e.target.value)); }}
            >
              {services.filter((s) => s.enabled !== false).map((s) => (
                <MenuItem key={s.id} value={s.id}>{s.name || s.code}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {mode === 'SERVICE' && !serviceId && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Select a service — calls on this campaign will be scored with that service's active scorecard.
          </Alert>
        )}
        {mode === 'SERVICE' && serviceId && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Calls on this campaign resolve to the selected service's active scorecard.
          </Alert>
        )}
        {mode === 'CAMPAIGN' && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Selecting Campaign creates/uses a versioned card cloned from Global as a starting template.
            Enabled positive weights: {weightPreview} / 100
          </Alert>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid="campaign-desktop-recording">
        <Typography variant="subtitle2" gutterBottom>Desktop Recording Policy (fallback only)</Typography>
        <Alert severity="warning" sx={{ mb: 1 }}>
          Provider-native recording remains preferred. Desktop capture is off by default and requires
          explicit acknowledgement that campaign recording policy has been reviewed/approved.
          TEKS does not determine legal consent requirements automatically.
        </Alert>
        <FormControlLabel
          control={(
            <Switch
              checked={recordingAllowed}
              disabled={!canManage}
              onChange={(e) => { setSaved(false); setRecordingAllowed(e.target.checked); }}
            />
          )}
          label="Recording Allowed"
        />
        <FormControlLabel
          control={(
            <Switch
              checked={desktopEnabled}
              disabled={!canManage}
              onChange={(e) => { setSaved(false); setDesktopEnabled(e.target.checked); }}
            />
          )}
          label="Desktop Call Recording Enabled"
        />
        <FormControlLabel
          control={(
            <Switch
              checked={desktopConsent}
              disabled={!canManage}
              onChange={(e) => { setSaved(false); setDesktopConsent(e.target.checked); }}
            />
          )}
          label="Campaign recording policy has been reviewed/approved"
        />
        <FormControl size="small" sx={{ minWidth: 240, display: 'block', mt: 1 }}>
          <InputLabel>Capture mode</InputLabel>
          <Select
            label="Capture mode"
            value={desktopMode}
            disabled={!canManage}
            onChange={(e) => { setSaved(false); setDesktopMode(String(e.target.value)); }}
          >
            <MenuItem value="DISABLED">DISABLED</MenuItem>
            <MenuItem value="FALLBACK_ONLY">FALLBACK_ONLY</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 240, display: 'block', mt: 1 }}>
          <InputLabel>Recording source</InputLabel>
          <Select
            label="Recording source"
            value={recordingSource}
            disabled={!canManage}
            onChange={(e) => { setSaved(false); setRecordingSource(String(e.target.value)); }}
          >
            <MenuItem value="PROVIDER_FIRST">PROVIDER_FIRST</MenuItem>
            <MenuItem value="DESKTOP_FALLBACK">DESKTOP_FALLBACK</MenuItem>
            <MenuItem value="MANUAL_ONLY">MANUAL_ONLY</MenuItem>
          </Select>
        </FormControl>
        <TextField
          fullWidth
          size="small"
          sx={{ mt: 1 }}
          label="Softphone process allowlist"
          value={softphoneAllowlist}
          disabled={!canManage}
          onChange={(e) => { setSaved(false); setSoftphoneAllowlist(e.target.value); }}
          helperText="Comma-separated process names from configuration — do not hard-code vendors."
        />
        <TextField
          size="small"
          type="number"
          sx={{ mt: 1, width: 220 }}
          label="Random review sample %"
          value={samplePercent}
          disabled={!canManage}
          onChange={(e) => { setSaved(false); setSamplePercent(Number(e.target.value)); }}
        />
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid="campaign-thresholds">
        <Typography variant="subtitle2" gutterBottom>Thresholds</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Minimum Passing Score"
              value={minPass}
              disabled={!canManage}
              onChange={(e) => { setSaved(false); setMinPass(Number(e.target.value)); }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Review Threshold"
              value={reviewAt}
              disabled={!canManage}
              onChange={(e) => { setSaved(false); setReviewAt(Number(e.target.value)); }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Critical Failure Behavior</InputLabel>
              <Select
                label="Critical Failure Behavior"
                value={critBehavior}
                disabled={!canManage}
                onChange={(e) => { setSaved(false); setCritBehavior(e.target.value as any); }}
              >
                <MenuItem value="FAIL">Force FAIL</MenuItem>
                <MenuItem value="REVIEW">Force REVIEW</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid="campaign-compliance-rules">
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Compliance rules</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Findings are review prompts. Serious and Critical stay in the review queue and notify through the existing Call QA alerts.
        </Typography>
        {COMPLIANCE_RULES.map(([id, label]) => (
          <Box key={id} display="flex" gap={2} alignItems="center" sx={{ mb: 0.5 }}>
            <FormControlLabel
              control={(
                <Switch
                  checked={complianceRules[id]?.enabled !== false}
                  disabled={!canManage}
                  onChange={(e) => {
                    setSaved(false);
                    setComplianceRules((prev) => ({
                      ...prev,
                      [id]: { enabled: e.target.checked, severity: prev[id]?.severity || 'WARNING' },
                    }));
                  }}
                />
              )}
              label={label}
              sx={{ minWidth: 360, m: 0 }}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select
                value={complianceRules[id]?.severity || 'WARNING'}
                disabled={!canManage}
                onChange={(e) => {
                  setSaved(false);
                  setComplianceRules((prev) => ({
                    ...prev,
                    [id]: { enabled: prev[id]?.enabled !== false, severity: String(e.target.value) },
                  }));
                }}
              >
                <MenuItem value="WARNING">Warning</MenuItem>
                <MenuItem value="SERIOUS">Serious</MenuItem>
                <MenuItem value="CRITICAL">Critical</MenuItem>
              </Select>
            </FormControl>
          </Box>
        ))}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid="campaign-phrase-rules">
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Phrase / Keyword Monitoring</Typography>
          <FormControlLabel
            control={(
              <Switch
                checked={phraseOn}
                disabled={!canManage}
                onChange={(e) => { setSaved(false); setPhraseOn(e.target.checked); }}
              />
            )}
            label="Enabled"
          />
        </Box>
        <Alert severity="info" sx={{ mb: 1 }}>
          Matches mean Review Recommended — not automatic guilt or violation.
        </Alert>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Name', 'Category', 'Match', 'Speaker', 'Severity', 'Phrases'].map((h) => (
                  <TableCell key={h}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.map((r, idx) => (
                <TableRow key={r.id || idx}>
                  <TableCell>
                    <TextField
                      size="small"
                      value={r.name || ''}
                      disabled={!canManage}
                      onChange={(e) => {
                        setSaved(false);
                        setRules((prev) => {
                          const next = [...prev];
                          next[idx] = { ...next[idx], name: e.target.value };
                          return next;
                        });
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={r.category || 'CUSTOM'}
                      disabled={!canManage}
                      onChange={(e) => {
                        setSaved(false);
                        setRules((prev) => {
                          const next = [...prev];
                          next[idx] = { ...next[idx], category: e.target.value };
                          return next;
                        });
                      }}
                    >
                      {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={r.match_type || 'KEYWORD'}
                      disabled={!canManage}
                      onChange={(e) => {
                        setSaved(false);
                        setRules((prev) => {
                          const next = [...prev];
                          next[idx] = { ...next[idx], match_type: e.target.value };
                          return next;
                        });
                      }}
                    >
                      {MATCH_TYPES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={r.speaker_scope || 'ANY'}
                      disabled={!canManage}
                      onChange={(e) => {
                        setSaved(false);
                        setRules((prev) => {
                          const next = [...prev];
                          next[idx] = { ...next[idx], speaker_scope: e.target.value };
                          return next;
                        });
                      }}
                    >
                      {SPEAKERS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={r.severity || 'warning'}
                      disabled={!canManage}
                      onChange={(e) => {
                        setSaved(false);
                        setRules((prev) => {
                          const next = [...prev];
                          next[idx] = { ...next[idx], severity: e.target.value };
                          return next;
                        });
                      }}
                    >
                      {['info', 'warning', 'serious', 'critical'].map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                    </Select>
                  </TableCell>
                  <TableCell sx={{ minWidth: 200 }}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="comma-separated"
                      value={r.phrasesText || ''}
                      disabled={!canManage}
                      onChange={(e) => {
                        setSaved(false);
                        setRules((prev) => {
                          const next = [...prev];
                          next[idx] = { ...next[idx], phrasesText: e.target.value };
                          return next;
                        });
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {canManage && (
          <Button
            size="small"
            sx={{ mt: 1 }}
            onClick={() => {
              setSaved(false);
              setRules((prev) => [...prev, {
                name: 'New rule',
                category: 'RISK',
                match_type: 'KEYWORD',
                speaker_scope: 'ANY',
                severity: 'warning',
                phrasesText: '',
                enabled: true,
              }]);
            }}
          >
            Add phrase rule
          </Button>
        )}
      </Paper>

      {canManage && (
        <Button
          variant="contained"
          disabled={saveMutation.isPending}
          onClick={() => saveMutation.mutate()}
        >
          Save Campaign QA Config
        </Button>
      )}
    </Box>
  );
};

export default CallQaCampaignsPage;
